import { Button } from "@jti/core-components/components/Button";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { getFormattedPriceForQuantity } from "../../utils/getFormattedPriceForQuantity";
import { handleError } from "../../utils/handleError";
import { ProductDetailsCtaLayout } from "../ProductDetailsCtaLayout";
import { ProductDetailsCtaMobileProductData } from "../ProductDetailsCtaMobileProductData";

import styles from "./SticksFormSubmitButton.module.scss";

type SticksFormSubmitButtonProps = {
  productName: string;
  finalPrices: { value: number; currency: string; multiplier: number }[];
  quantity: number;
  isProductDataLoading: boolean;
  isAddProductInProgress: boolean;
  isOutOfStockProductSelected: boolean;
  isProductDataLoaded: boolean;
  isCartLoaded: boolean;
  showPricePerMonthLabel: boolean;
  isQuantityLimitReached: boolean;
  customBuyCtaLabel?: string;
};

export const SticksFormSubmitButton = ({
  productName,
  finalPrices,
  quantity,
  isProductDataLoading,
  isAddProductInProgress,
  isProductDataLoaded,
  isOutOfStockProductSelected,
  isCartLoaded,
  showPricePerMonthLabel,
  customBuyCtaLabel,
  isQuantityLimitReached,
}: SticksFormSubmitButtonProps) => {
  const { __ } = useTranslations();
  let calculatedFinalPrice;
  const baseLabel = customBuyCtaLabel || __("com.product.add_to_cart_button_label");
  const canCalculateFinalPrices = !isProductDataLoading && isProductDataLoaded;

  if (canCalculateFinalPrices) {
    calculatedFinalPrice = getFormattedPriceForQuantity(finalPrices, quantity);
  }

  if (canCalculateFinalPrices && !calculatedFinalPrice) {
    handleError(`Cannot calculate final price for quantity: ${quantity}`);
  }

  const labelWithPrice = calculatedFinalPrice
    ? `${baseLabel}(${calculatedFinalPrice}${showPricePerMonthLabel ? "/" + __("product.info.monthlyPriceLabel") : ""})`
    : baseLabel;

  const buttonProps = {
    type: "submit" as const,
    pending: isAddProductInProgress,
    disabled: isAddProductInProgress || isOutOfStockProductSelected || !isCartLoaded || isQuantityLimitReached,
    "data-testid": "sticks-form-submit-button",
  };

  return (
    <ProductDetailsCtaLayout
      mobileCtaContent={
        <ProductDetailsCtaMobileProductData
          productName={productName}
          productPrice={calculatedFinalPrice}
          showPricePerMonthLabel={showPricePerMonthLabel}
        />
      }
    >
      <>
        <span className={styles.buttonWithoutPriceWrapper}>
          <Button {...buttonProps}>{baseLabel}</Button>
        </span>
        <span className={styles.buttonWithPriceWrapper}>
          <Button {...buttonProps}>{labelWithPrice}</Button>
        </span>
      </>
    </ProductDetailsCtaLayout>
  );
};
